import React, { memo } from "react";
import { cloudImg } from "../services/api";
import { formatPrice } from "../utils/currency";

// ----------------------------------------------------------------------
// Compact horizontal recommendation strip. Reused twice on the customer
// menu page — once for "Recommended by Chef" (gold accent) and once for
// "Loved by our customers" (teal accent).
//
// Performance note: this used to receive the entire ``cart`` object as
// a prop, which meant every card on every strip re-rendered each time
// any item was added — visibly slow on low-end phones, especially in
// the field. We now extract a memoized ``RecommendationCard`` and pass
// only the per-item count plus a stable ``onAdd`` callback (created
// with useCallback in MenuPage). Siblings whose count didn't change
// don't re-render, and the tap feels instant.
//
// Also: a CSS active-scale animation gives the user immediate visual
// confirmation even on a sluggish device, before React's state pass
// has finished.
// ----------------------------------------------------------------------
const RecommendationCard = memo(function RecommendationCard({
  item,
  count,
  onAdd,
  accentColor,
  badgeIcon,
  displayName,
  addLabel,
  showCaption,
  lang,
  currency,
}) {
  const img = cloudImg(item.image_url, 200);
  return (
    <button
      type="button"
      onClick={() => onAdd(item)}
      className="tap-feedback"
      style={{
        flex: "0 0 140px",
        background: "#fff",
        border: `1px solid ${accentColor}`,
        borderRadius: 12,
        padding: 8,
        cursor: "pointer",
        textAlign: "left",
        boxShadow: `0 2px 6px ${accentColor}33`,
        position: "relative",
      }}
      aria-label={`Add ${displayName} to order`}
    >
      <div
        style={{
          width: "100%",
          height: 88,
          borderRadius: 8,
          overflow: "hidden",
          background: "#f0ece6",
          marginBottom: 6,
        }}
      >
        {img ? (
          <img
            src={img}
            alt={displayName}
            loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div
            style={{
              width: "100%", height: "100%",
              display: "flex", alignItems: "center",
              justifyContent: "center", color: "#b9b2a4",
              fontWeight: 700, fontSize: "1.5rem",
            }}
          >
            {displayName.slice(0, 1)}
          </div>
        )}
      </div>
      <div
        style={{
          fontWeight: 700, fontSize: ".88rem",
          lineHeight: 1.25, color: "#1f2933",
          whiteSpace: "nowrap", overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        lang={lang}
      >
        <span style={{ color: accentColor }}>{badgeIcon} </span>
        {displayName}
      </div>
      {showCaption && item.chef_pick_note && (
        <div
          style={{
            fontSize: ".7rem", color: "#7e671f",
            fontStyle: "italic", marginTop: 2,
            whiteSpace: "nowrap", overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.chef_pick_note}
        </div>
      )}
      <div
        style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "center", marginTop: 6,
        }}
      >
        <span style={{ fontWeight: 700, fontSize: ".88rem" }}>
          {formatPrice(item.price, currency)}
        </span>
        <span
          style={{
            background: count > 0 ? "#008080" : accentColor,
            color: "#fff",
            padding: "2px 8px",
            borderRadius: 999,
            fontSize: ".75rem",
            fontWeight: 700,
          }}
        >
          {count > 0 ? `× ${count}` : `+ ${addLabel}`}
        </span>
      </div>
    </button>
  );
});

function RecommendationStrip({
  keyPrefix,
  title,
  subtitle,
  accentColor,
  badgeIcon,
  items,
  cart,
  onAdd,
  pickName,
  t,
  lang,
  showCaption = false,
  currency,
}) {
  const addLabel = t("add");
  return (
    <section style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
        <h2
          className="category"
          style={{
            display: "flex", alignItems: "center", gap: 6,
            margin: 0, color: accentColor,
          }}
        >
          <span>{badgeIcon}</span>
          {title}
        </h2>
        {subtitle && (
          <span
            style={{
              fontSize: ".82rem", color: "#5c7c7f",
              fontStyle: "italic",
            }}
          >
            · {subtitle}
          </span>
        )}
      </div>
      <div
        style={{
          display: "flex",
          gap: 10,
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          paddingBottom: 6,
          margin: "0 -4px",
        }}
      >
        {items.map((item) => (
          <RecommendationCard
            key={`${keyPrefix}-${item.id}`}
            item={item}
            count={cart[item.id]?.quantity || 0}
            onAdd={onAdd}
            accentColor={accentColor}
            badgeIcon={badgeIcon}
            displayName={pickName(item)}
            addLabel={addLabel}
            showCaption={showCaption}
            currency={currency}
            lang={lang}
          />
        ))}
      </div>
    </section>
  );
}

// RECOMMENDATION STRIPS
// Two distinct labelled rows at the top of the menu:
//   1. "Recommended by Chef" — manual curation (chef picks)
//   2. "Loved by our customers" — auto top-sellers (30d)
// Both hidden when empty (brand-new restaurant) or when the
// guest has active dietary filters (recommendations don't
// respect filters, so we hide rather than mislead).
export default function RecommendationStrips({
  chefPicks,
  topSellers,
  tagFilters,
  cart,
  onAdd,
  pickName,
  t,
  lang,
  currency,
}) {
  return (
    <>
      {chefPicks.length > 0 && tagFilters.size === 0 && (
        <RecommendationStrip
          keyPrefix="chef"
          title={
            lang === "nl" ? "Aanbevolen door de chef" :
            lang === "am" ? "በዋና ሼፍ የተመረጠ" :
            lang === "om" ? "Chef irraa filatamoo" :
            lang === "sid" ? "Recommended by Chef" :
            "Recommended by Chef"
          }
          subtitle={
            lang === "nl" ? "Vandaag's keuze van de keuken" :
            lang === "am" ? "የዛሬው ምርጥ" :
            lang === "om" ? "Filannoo har'aa" :
            lang === "sid" ? "Today's pick" :
            "Today's pick"
          }
          accentColor="#d4af37"      /* gold */
          badgeIcon="⭐"
          items={chefPicks}
          cart={cart}
          onAdd={onAdd}
          pickName={pickName}
          t={t}
          lang={lang}
          showCaption  /* show chef_pick_note under the name */
          currency={currency}
        />
      )}
      {topSellers.length > 0 && tagFilters.size === 0 && (
        <RecommendationStrip
          keyPrefix="top"
          title={
            lang === "nl" ? "Geliefd bij onze gasten" :
            lang === "am" ? "በደንበኞቻችን የተወደደ" :
            lang === "om" ? "Keessummoota keenyaan jaalatamaa" :
            lang === "sid" ? "Loved by our customers" :
            "Loved by our customers"
          }
          subtitle={
            lang === "nl" ? "Meest bestelde gerechten" :
            lang === "am" ? "በብዛት የተዘዙ ምግቦች" :
            lang === "om" ? "Nyaata baay'inaan ajajaman" :
            lang === "sid" ? "Most ordered" :
            "Most ordered"
          }
          accentColor="#008080"      /* teal */
          badgeIcon="❤"
          items={topSellers}
          cart={cart}
          onAdd={onAdd}
          pickName={pickName}
          t={t}
          lang={lang}
          currency={currency}
        />
      )}
    </>
  );
}
